import { useEffect, useMemo, useState } from 'react'
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { Layout, Tabs, type TabChangeEventDetail } from '@loadsmart/miranda-react'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { Icon } from 'core/ui/components/icon/Icon'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { useDisableSalesforceChat } from 'core/ui/hooks/useDisableSalesforceChat'
import useWindowWidth from 'core/ui/hooks/useWindowWidth'
import { MainContent } from 'core/ui/MainContent'

import { intSizes, sizes } from 'legacy/styles/screen'

import { useSaveAgnosticUserToken } from 'auth/ui/hooks/useSaveAgnosticUserToken'

import { CarrierInfo } from './CarrierInfo'
import { EmptyShipmentPageState } from './EmptyShipmentPageState'
import { useShipment } from './hooks/useShipment'
import { useShipmentChargesPermissions } from './hooks/useShipmentChargesPermissions'
import { ShipmentAnalyticsProvider } from './ShipmentAnalyticsContext'
import { ShipmentCharges } from './ShipmentCharges'
import { ShipmentDetailsBreadcrumb } from './ShipmentDetailsBreadcrumb'
import { ShipmentInfo } from './ShipmentInfo'
import { TrackAndTrace } from './TrackAndTrace'
import SignUpUserNotOnboardedBanner from './tracking/onboarding/SignUpUserNotOnboardedBanner'
import { TrackingOnboardingProvider } from './tracking/onboarding/TrackingOnboardingContext'
import UserNotOnboardedBanner from './tracking/onboarding/UserNotOnboardedBanner'
import UserNotOnboardedDialog from './tracking/onboarding/UserNotOnboardedDialog'
import UserOnboardedBanner from './tracking/onboarding/UserOnboardedBanner'
import UserOnboardedDialog from './tracking/onboarding/UserOnboardedDialog'

const SHIPMENT_DETAILS_PATH = '/all-in-one/shipment/:shipmentUUID/:section?'
export const shipmentDetailsTabs = {
  trackAndTrace: {
    value: 'track-and-trace',
    label: 'Track and trace',
  },
  charges: {
    value: 'charges',
    label: 'Charges',
  },
} as const

type ShipmentDetailsTab = (typeof shipmentDetailsTabs)[keyof typeof shipmentDetailsTabs]['value']

export function ShipmentDetails() {
  useDisableSalesforceChat()
  useSaveAgnosticUserToken()

  const history = useHistory()
  const location = useLocation()
  const match = useRouteMatch<{ shipmentUUID: string; section?: string }>()
  const { shipmentUUID } = match.params

  const { shipment, shipmentStatus, fetchShipment, refetchShipment } = useShipment()

  const { isShipmentChargesEnabled } = useShipmentChargesPermissions(shipment)

  const [activeTab, setActiveTab] = useState<ShipmentDetailsTab>(
    () => (match.params.section as ShipmentDetailsTab) ?? shipmentDetailsTabs.trackAndTrace.value
  )
  const [isCardCollapsed, setIsCardCollapsed] = useState(
    activeTab === shipmentDetailsTabs.charges.value
  )

  const abortController = useAbortController()
  const isMobile = useWindowWidth() < intSizes.md

  useEffect(() => {
    fetchShipment(shipmentUUID, abortController)

    return () => {
      abortController.abort()
    }
  }, [shipmentUUID, fetchShipment, abortController])

  function onTabChange(event: CustomEvent<TabChangeEventDetail>) {
    const tab = event.detail.value as unknown as ShipmentDetailsTab

    const selectedTabURL = generatePath(SHIPMENT_DETAILS_PATH, {
      shipmentUUID,
      section: tab,
    })

    setIsCardCollapsed(tab === shipmentDetailsTabs.charges.value)
    setActiveTab(tab)
    history.push({ pathname: selectedTabURL, search: location.search })
  }

  const analyticsProperties = useMemo(
    () => ({
      carrier: shipment?.carrier,
      shipper: shipment?.shipper,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shipment?.uuid]
  )

  return (
    <ShipmentAnalyticsProvider properties={analyticsProperties}>
      <MainContent>
        <AsyncContent
          status={shipmentStatus}
          loadingState={<LoadingState />}
          errorState={<EmptyShipmentPageState />}
        >
          {shipment && (
            <Layout.Stack gap="spacing-6" style={{ marginTop: '2em' }}>
              <TrackingOnboardingProvider>
                <Layout.Group justify="space-between">
                  <ShipmentDetailsBreadcrumb
                    shipmentTitle={shipment.shipperGuideId}
                    shipmentUUID={shipmentUUID}
                  />

                  <CarrierInfo carrier={shipment.carrier} />
                </Layout.Group>
                <Layout.Stack gap="spacing-2">
                  <UserNotOnboardedBanner />
                  <UserOnboardedBanner />
                  <SignUpUserNotOnboardedBanner />
                  <Layout.Sidebar
                    sideWidth="443px"
                    minContentWidth={isMobile ? sizes.sm : '500px'}
                    style={{ flexDirection: 'row' }}
                  >
                    <Layout.Sidebar.Side style={{ paddingLeft: 0, paddingRight: 0 }}>
                      <ShipmentInfo
                        shipment={shipment}
                        collapsed={isCardCollapsed}
                        refetchShipment={refetchShipment}
                      />
                    </Layout.Sidebar.Side>
                    <Layout.Sidebar.Content style={{ paddingLeft: 0, paddingRight: 0 }}>
                      {isShipmentChargesEnabled ? (
                        <Tabs activeTab={activeTab} onTabChange={onTabChange}>
                          <Tabs.Tab
                            name={shipmentDetailsTabs.trackAndTrace.value}
                            leading={
                              isMobile ? null : <Icon name="truck-dryvan" width={16} height={16} />
                            }
                          >
                            {shipmentDetailsTabs.trackAndTrace.label}
                          </Tabs.Tab>
                          <Tabs.Tab
                            name={shipmentDetailsTabs.charges.value}
                            leading={
                              isMobile ? null : <Icon name="invoice" width={16} height={16} />
                            }
                          >
                            {shipmentDetailsTabs.charges.label}
                          </Tabs.Tab>
                          <Tabs.Panel forTab={shipmentDetailsTabs.trackAndTrace.value}>
                            <TrackAndTrace shipment={shipment} refetchShipment={refetchShipment} />
                          </Tabs.Panel>
                          <Tabs.Panel forTab={shipmentDetailsTabs.charges.value}>
                            <ShipmentCharges shipment={shipment} />
                          </Tabs.Panel>
                        </Tabs>
                      ) : (
                        <TrackAndTrace shipment={shipment} refetchShipment={refetchShipment} />
                      )}
                    </Layout.Sidebar.Content>
                  </Layout.Sidebar>
                </Layout.Stack>
                <UserNotOnboardedDialog />
                <UserOnboardedDialog />
              </TrackingOnboardingProvider>
            </Layout.Stack>
          )}
        </AsyncContent>
      </MainContent>
    </ShipmentAnalyticsProvider>
  )
}
